import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Card, CardContent, Typography, Button, Alert } from '@mui/material';

function PasscodeAccess() {
    const location = useLocation();
    const [accessData, setAccessData] = useState(null);
    const [isExpired, setIsExpired] = useState(false);
    const [timeRemaining, setTimeRemaining] = useState(0);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchAccessData = async () => {
            try {
                const params = new URLSearchParams(location.search);
                const reservationId = params.get('reservationId');

                if (!reservationId) {
                    setError('❌ Invalid or missing access link.');
                    setLoading(false);
                    return;
                }

                const teableBaseUrl = process.env.REACT_APP_TEABLE_BASE_URL || 'https://teable.namuve.com';

                // Use search API to directly find the record by Reservation ID
                const searchUrl = `${teableBaseUrl}/api/table/tblSe92yX9IzuAgnWDV/record?search=${reservationId}&search=Hostaway+Reservation+ID&search=true`;

                const response = await fetch(searchUrl, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${process.env.REACT_APP_TEABLE_API_TOKEN_PASSCODE_SEARCH}`,
                    },
                });

                if (!response.ok) {
                    throw new Error('Failed to fetch data from Teable');
                }

                const data = await response.json();
                const records = data.records || [];

                if (records.length === 0) {
                    setError('❌ No matching passcode record found.');
                    setLoading(false);
                    return;
                }


                const matchedRecord = records[0]; // Use first matching record
                const guestName = matchedRecord.fields['Guest Name'] || 'Guest';
                const listingName = matchedRecord.fields['Listing Name'] || 'Your Apartment';
                const lockCode = matchedRecord.fields['Lock Code'];

                // ✅ Validate that lock code exists
                if (!lockCode || lockCode.trim() === '') {
                    setError('❌ No lock code available for this reservation.');
                    setLoading(false);
                    return;
                }

                // Get checkIn and checkOut from URL parameters FIRST, fallback to Teable
                const urlParams = new URLSearchParams(location.search);
                const urlCheckIn = urlParams.get('checkIn');
                const urlCheckOut = urlParams.get('checkOut');

                const checkIn = urlCheckIn || matchedRecord.fields['Check-In Date and Time'] || null;
                const checkOut = urlCheckOut || matchedRecord.fields['Check-Out Date and Time'] || null;

                console.log('📅 Check-in:', checkIn, '(from', urlCheckIn ? 'URL' : 'Teable', ')');
                console.log('📅 Check-out:', checkOut, '(from', urlCheckOut ? 'URL' : 'Teable', ')');

                // Read expiration from URL first, then fallback to Teable
                let expirationTime = null;
                const urlExpires = urlParams.get('expires');

                if (urlExpires) {
                    expirationTime = parseInt(urlExpires);
                    console.log('⏰ Found expiration in URL:', expirationTime);
                } else {
                    // Fetch QR expiration from Table 2 (Teable) - using search query for reliability
                    try {
                        const table2SearchUrl = `${teableBaseUrl}/api/table/tblewe7HY1LFBRGsl7T/record?search=${reservationId}&search=Reservation+ID&search=true`;
                        const table2Res = await fetch(table2SearchUrl, {
                            headers: {
                                Authorization: `Bearer ${process.env.REACT_APP_TEABLE_API_TOKEN_PASSCODE_QR}`,
                            }
                        });

                        if (table2Res.ok) {
                            const table2Data = await table2Res.json();
                            const qrRecord = table2Data.records?.[0]; // Get the first result from search

                            if (qrRecord && qrRecord.fields['QR Expiration']) {
                                expirationTime = parseInt(qrRecord.fields['QR Expiration']);
                                console.log('✅ Found QR expiration in Teable via search:', expirationTime);
                            }
                        }
                    } catch (err) {
                        console.error('Error fetching QR expiration from Teable search:', err);
                    }
                }


                // Check if expired
                if (expirationTime && Date.now() > expirationTime) {
                    setIsExpired(true);
                    setLoading(false);
                    return;
                }

                setAccessData({
                    guestName,
                    listingName,
                    lockCode,
                    checkIn: checkIn,
                    checkOut: checkOut,
                });
                setLoading(false);
                console.log('✅ Found matching record:', matchedRecord);

                // Start timer if we have expiration
                if (expirationTime) {
                    const timer = setInterval(() => {
                        const now = Date.now();
                        const remaining = expirationTime - now;

                        if (remaining <= 0) {
                            setIsExpired(true);
                            clearInterval(timer);
                            return;
                        }

                        setTimeRemaining(remaining);
                    }, 1000);

                    return () => clearInterval(timer);
                }
            } catch (err) {
                console.error('❌ Error fetching access data:', err);
                setError('Failed to load passcode information.');
                setLoading(false);
            }
        };

        fetchAccessData();
    }, [location.search]);

    if (isExpired) {
        return (
            <Box sx={{ minHeight: '100vh', background: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
                <Card sx={{ maxWidth: 400, textAlign: 'center', p: 4, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                    <Typography variant="h5" sx={{ color: '#7f1d1d', fontWeight: 700, mb: 2 }}>
                        ⏰ Link Expired
                    </Typography>
                    <Typography sx={{ color: '#6b7280', fontSize: 15 }}>
                        This access link has expired. Please contact support for a new link.
                    </Typography>
                </Card>
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ minHeight: '100vh', background: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
                <Card sx={{ maxWidth: 400, textAlign: 'center', p: 4, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                    <Typography variant="h5" sx={{ color: '#7f1d1d', fontWeight: 700, mb: 2 }}>
                        ❌ Error
                    </Typography>
                    <Typography sx={{ color: '#6b7280', fontSize: 15 }}>
                        {error}
                    </Typography>
                </Card>
            </Box>
        );
    }

    if (loading || !accessData) {
        return (
            <Box sx={{ minHeight: '100vh', background: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="h5" sx={{ color: '#6b7280' }}>
                    Loading...
                </Typography>
            </Box>
        );
    }

    // Helper function to format date and time
    const formatDateTime = (dateTimeString) => {
        if (!dateTimeString) return '';
        const date = new Date(dateTimeString);
        const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        const month = months[date.getMonth()];
        const day = date.getDate();
        let hours = date.getHours();
        const minutes = date.getMinutes().toString().padStart(2, '0');
        const ampm = hours >= 12 ? 'PM' : 'AM';
        hours = hours % 12 || 12;
        return `${month} ${day}, ${hours}:${minutes} ${ampm}`;
    };

    // Helper function to format time remaining
    const formatTime = (ms) => {
        const totalSeconds = Math.floor(ms / 1000);
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    };

    return (
        <Box sx={{
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: { xs: 3, sm: 4 },
            fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
        }}>
            <Card sx={{
                maxWidth: { xs: '100%', sm: 700 },
                width: '100%',
                boxShadow: '0 10px 40px rgba(0,0,0,0.06)',
                borderRadius: '20px',
                border: '1px solid #e2e8f0',
                background: '#ffffff',
                overflow: 'hidden'
            }}>
                <CardContent sx={{ p: { xs: 4, sm: 5 }, pb: { xs: 13, sm: 5 } }}>
                    <Box sx={{ textAlign: 'center', mb: { xs: 2, sm: 3 } }}>
                        <Box
                            component="img"
                            src="/img/custom-logo.png"
                            alt="NAMUVE"
                            sx={{
                                height: { xs: '120px', sm: '180px' },
                                width: 'auto',
                                objectFit: 'contain'
                            }}
                        />
                    </Box>

                    {/* Welcome Message */}
                    <Box sx={{ textAlign: 'center', mb: { xs: 5, sm: 6 } }}>
                        <Typography sx={{
                            fontSize: { xs: 24, sm: 42 },
                            fontWeight: 400,
                            color: '#64748b',
                            mb: 0.5,
                            lineHeight: 1.2,
                            fontFamily: '"Inter", sans-serif'
                        }}>
                            Welcome
                        </Typography>
                        <Typography sx={{
                            fontSize: { xs: 24, sm: 42 },
                            fontWeight: 800,
                            color: '#1e293b',
                            mb: 1.5,
                            lineHeight: 1.2,
                            fontFamily: '"Inter", sans-serif'
                        }}>
                            {accessData.guestName}
                        </Typography>

                        <Typography sx={{
                            fontSize: { xs: 16, sm: 18 },
                            color: '#64748b',
                            lineHeight: 1.6,
                            fontFamily: '"Inter", sans-serif'
                        }}>
                            Your apartment{' '}
                            <Box component="span" sx={{
                                color: '#3b82f6',
                                fontWeight: 700,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.5,
                                px: 1.5,
                                py: 0.5,
                                borderRadius: '8px',
                                background: '#eff6ff'
                            }}>
                                <Box component="span" sx={{ fontSize: 16 }}>✓</Box>
                                {accessData.listingName}
                            </Box>
                            {' '}is ready for your stay
                        </Typography>
                    </Box>

                    {/* Passcode Card - Hero Element */}
                    <Box sx={{
                        background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
                        borderRadius: '24px',
                        p: { xs: 4, sm: 5 },
                        mb: { xs: 5, sm: 6 },
                        border: '2px solid #bbf7d0',
                        position: 'relative',
                        overflow: 'hidden',
                        boxShadow: '0 8px 24px rgba(16, 185, 129, 0.12)',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: '4px',
                            background: 'linear-gradient(90deg, #10b981, #059669)',
                        }
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                            <Box component="span" sx={{ fontSize: { xs: 48, sm: 56 }, mr: 1.5 }}>🔒</Box>
                            <Typography sx={{
                                fontSize: { xs: 13, sm: 14 },
                                fontWeight: 700,
                                color: '#065f46',
                                letterSpacing: '1.5px',
                                textTransform: 'uppercase',
                                fontFamily: '"Inter", sans-serif'
                            }}>
                                SmartLock Passcode
                            </Typography>
                        </Box>

                        <Typography sx={{
                            fontSize: { xs: 36, sm: 64 },
                            fontWeight: 800,
                            color: '#059669',
                            textAlign: 'center',
                            fontFamily: '"SF Mono", "Monaco", "Consolas", monospace',
                            letterSpacing: { xs: '2px', sm: '8px' },
                            textShadow: '0 2px 4px rgba(5, 150, 105, 0.1)',
                            mb: 3,
                            userSelect: 'text'
                        }}>
                            {accessData.lockCode}#
                        </Typography>
                        {/* Copy Passcode Button */}
                        <Button
                            fullWidth
                            variant="outlined"
                            onClick={() => {
                                const copyText = `Welcome, ${accessData.guestName}! ✨\n\nYour apartment ${accessData.listingName} is all set for your stay. Use the Smartlock passcode ${accessData.lockCode}# to enter — it's valid from ${formatDateTime(accessData.checkIn)} till ${formatDateTime(accessData.checkOut)}.\n\nYour comfort means everything to us — feel free to reach out via this WhatsApp contact if you need anything at all.\n\n– Team NAMUVE`;
                                navigator.clipboard.writeText(copyText);
                            }}
                            startIcon={<Box component="span" sx={{ fontSize: 20 }}>📋</Box>}
                            sx={{
                                borderRadius: '12px',
                                py: 1.5,
                                minHeight: 48,
                                color: '#059669',
                                borderColor: '#059669',
                                fontWeight: 600,
                                fontSize: { xs: 15, sm: 16 },
                                textTransform: 'none',
                                fontFamily: '"Inter", sans-serif',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    background: '#f0fdf4',
                                    borderColor: '#047857',
                                    transform: 'translateY(-1px)',
                                    boxShadow: '0 4px 12px rgba(5, 150, 105, 0.15)'
                                },
                                '&:active': {
                                    transform: 'translateY(0)'
                                }
                            }}
                        >
                            Copy Passcode
                        </Button>
                    </Box>


                    {/* Validity Period */}
                    <Box sx={{
                        background: '#F9FAFB',
                        borderRadius: '16px',
                        border: '1px solid #E5E7EB',
                        p: { xs: 3, sm: 4 },
                        mb: { xs: 5, sm: 6 }
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1.5 }}>
                            <Box component="span" sx={{ fontSize: { xs: 24, sm: 28 } }}>📅</Box>
                            <Typography sx={{ fontSize: { xs: 12, sm: 13 }, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', fontFamily: '"Inter", sans-serif' }}>
                                Valid Period
                            </Typography>
                        </Box>

                        <Typography sx={{ fontSize: { xs: 11, sm: 18 }, color: '#475569', textAlign: 'center', lineHeight: 1.6, fontFamily: '"Inter", sans-serif', whiteSpace: 'nowrap' }}>
                            <Box component="span" sx={{ fontWeight: 700, color: '#1e293b' }}>
                                {formatDateTime(accessData.checkIn)}
                            </Box>
                            <Box component="span" sx={{ mx: 1, color: '#94a3b8' }}>→</Box>
                            <Box component="span" sx={{ fontWeight: 700, color: '#1e293b' }}>
                                {formatDateTime(accessData.checkOut)}
                            </Box>
                        </Typography>
                    </Box>



                    {/* Assistance Section */}
                    <Box sx={{ textAlign: 'center', mb: { xs: 4, sm: 5 } }}>
                        <Typography sx={{
                            fontSize: { xs: 16, sm: 18 },
                            color: '#64748b',
                            fontWeight: 600,
                            fontFamily: '"Inter", sans-serif'
                        }}>
                            Need assistance? We're here to help!
                        </Typography>
                    </Box>

                    {/* Floating WhatsApp Icon - Bottom Right */}
                    <Box
                        onClick={() => window.open('https://wa.me/923000454711', '_blank')}
                        sx={{
                            position: 'fixed',
                            bottom: { xs: 24, sm: 24 },
                            right: { xs: 12, sm: 24 },
                            width: { xs: 50, sm: 90 },
                            height: { xs: 50, sm: 90 },
                            borderRadius: '50%',
                            background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            boxShadow: '0 4px 20px rgba(37, 211, 102, 0.4)',
                            transition: 'all 0.3s ease',
                            zIndex: 1000,
                            '&:hover': {
                                transform: 'scale(1.1)',
                                boxShadow: '0 6px 28px rgba(37, 211, 102, 0.5)'
                            }
                        }}
                    >
                        <Box component="svg" sx={{ width: { xs: 30, sm: 50 }, height: { xs: 30, sm: 50 } }} viewBox="0 0 24 24" fill="white">
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                        </Box>
                    </Box>

                    {/* Expiry Status - Enhanced Design */}
                    {isExpired ? (
                        <Alert
                            severity="error"
                            sx={{
                                mt: 2,
                                borderRadius: '12px',
                                '& .MuiAlert-icon': { fontSize: 20 }
                            }}
                        >
                            This link has expired
                        </Alert>
                    ) : (
                        <Box sx={{
                            mt: 2,
                            borderRadius: '12px',
                            background: '#ffffff',
                            border: '2px solid #f3f4f6',
                            p: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1.5
                        }}>
                            {/* Clock Icon */}
                            <Box sx={{
                                width: 32,
                                height: 32,
                                borderRadius: '50%',
                                background: 'linear-gradient(135deg, #fb923c 0%, #f97316 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
                                    <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z" />
                                </svg>
                            </Box>

                            {/* Time Display */}
                            <Typography sx={{
                                fontSize: { xs: 20, sm: 24 },
                                fontWeight: 700,
                                color: '#1e293b',
                                fontFamily: '"Inter", sans-serif',
                                letterSpacing: '1px'
                            }}>
                                {(() => {
                                    const totalSeconds = Math.floor(timeRemaining / 1000);
                                    const hours = Math.floor(totalSeconds / 3600);
                                    const minutes = Math.floor((totalSeconds % 3600) / 60);
                                    const seconds = totalSeconds % 60;

                                    if (hours > 0) {
                                        return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
                                    } else {
                                        return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')} min${minutes !== 1 ? 's' : ''}`;
                                    }
                                })()}
                            </Typography>
                        </Box>
                    )}
                </CardContent>
            </Card>
        </Box >
    );
}

export default PasscodeAccess;
